/**
 * GoldFire Network (https://goldfire.me)
 * Copyright (c) 2012 - 2020, GoldFire Studios, Inc.
 * http://goldfirestudios.com
 */

import Vuex from 'vuex';
import {convertEmoji} from './utils';

// Handle all of the infinite scrolling hooks.
export const infiniteScrolling = {
  data() {
    return {
      maxPages: 3,
    };
  },
  beforeDestroy() {
    this.$store.commit('setScrollData', {
      direction: '',
      loading: false,
      start: true,
      end: false,
    });
  },
  computed: Vuex.mapState({
    start: state => state.scroll.start,
    end: state => state.scroll.end,
    direction: state => state.scroll.direction,
    scrollStart: state => state.panel.scrollStart,
    scrollEnd: state => state.panel.scrollEnd,
    limit() {
      const screenHeight = window ? window.screen.availHeight : 1080;
      const minRowHeight = this.infiniteScrollingCfg.minRowHeight || 104;
      const itemsPerRow = this.infiniteScrollingCfg.itemsPerRow || 1;
      const limit = Math.ceil((screenHeight / (minRowHeight / itemsPerRow)) * 1.25);

      // Make limit divisible by number of items per row.
      return Math.round(limit / itemsPerRow) * itemsPerRow;
    },
    items() {
      return this.infiniteScrollingCfg.items;
    },
  }),
  watch: {
    scrollStart(val) {
      if (val && !this.start) {
        this.load('prev');
      }
    },
    scrollEnd(val) {
      if (val && !this.end) {
        this.load('next');
      }
    },
  },
  methods: {
    /**
     * Purge pages from memory.
     * @param  {String}   options.purge  Whether to purge the 'first' or 'last' page
     * @param  {Number}   options.limit  Page length limit.
     * @param  {Array}    options.items  List from which to purge data.
     */
    doPurge({purge, limit, items}) {
      const startCount = items.length;

      if (purge === 'first') {
        items.splice(0, limit);
      } else if (purge === 'last') {
        items.splice(-limit);
      }

      // Notify the component of the merge.
      if (typeof this.purgeRes === 'function') {
        this.purgeRes(purge, startCount - items.length);
      }

      return items;
    },
    purge(purge, items) {
      const infItems = this.infiniteScrollingCfg.items;
      const maxItems = this.limit * this.maxPages;

      if (purge && infItems && infItems.length > maxItems) {
        this.$store.commit(this.infiniteScrollingCfg.mutationName, {items: this.doPurge({
          purge,
          limit: Math.min(items.length, infItems.length - maxItems),
          items: infItems.slice(),
        })});

        // Set start to false if we purged from the top.
        if (this.start && purge === 'first' && items.length > 0) {
          this.$store.commit('setScrollData', {start: false});
        } else if (this.end && purge === 'last' && items.length > 0) {
          this.$store.commit('setScrollData', {end: false});
        }
      }
    },
    load(direction, extra, purge) {
      if (!this.infiniteScrollingCfg || !this.infiniteScrollingCfg.actionName) {
        return Promise.resolve();
      }

      // Show the loader if we don't have any items yet.
      if (!this.infiniteScrollingCfg.items.length) {
        this.$store.commit('togglePanelLoading', true);
      }

      return new Promise((resolve) => {
        this.$store.commit('setScrollData', {loading: true, direction});

        // Cache the current height for scrolling adjustment.
        const currentHeight = this.$parent.$el.scrollHeight;

        // Based on the direction, determine the purge and key direction.
        const keys = this.infiniteScrollingCfg.keys || [];
        const key = {};

        if (this.direction === 'next') {
          purge = 'first';

          // Determine the value of the end key.
          const last = this.infiniteScrollingCfg.items[this.infiniteScrollingCfg.items.length - 1];
          if (last) {
            keys.forEach((val) => {
              key[val] = last[val];
            });
          }
        } else {
          purge = purge || 'last';

          // Determine the value of the start key.
          const first = this.infiniteScrollingCfg.items[0];
          if (first) {
            keys.forEach((val) => {
              key[val] = first[val];
            });
          }
        }

        // Determine what data to send.
        const {actionParams = {}, actionName} = this.infiniteScrollingCfg;
        const data = {limit: this.limit, direction, key};
        if (this.tags) {
          data.tags = this.tags;
        }
        const options = Object.assign(data, actionParams);
        let firstChild = this.$el.querySelector('.inf:first-of-type');
        let lastChild = this.$el.querySelector('.inf:last-of-type');
        const firstChildId = firstChild ? firstChild.id : undefined;
        const lastChildId = lastChild ? lastChild.id : undefined;

        // Call the action to load the new items.
        this.$store.dispatch(actionName, Object.assign(options, extra)).then(({items, end}) => {
          this.$store.commit('togglePanelLoading', false);

          // Set whether we are at the start or end.
          this.$store.commit('setScrollData', {
            start: end === 'start' ? true : this.start,
            end: end === 'end' ? true : this.end,
          });

          // Purge the items that are now scrolled away from.
          this.purge(purge, items);

          // Wait a tick and then update the scroll.
          this.$nextTick(() => {
            // Update loading and direction.
            this.$store.commit('setScrollData', {
              direction: '',
              loading: false,
            });

            if (typeof this.infiniteScrollingCfg.onLoaded === 'function') {
              this.infiniteScrollingCfg.onLoaded();
            }

            if (typeof this.loadRes === 'function') {
              this.loadRes({items, currentHeight, direction});
            }

            if (!firstChildId && !lastChildId) {
              return resolve();
            }

            if (direction === 'next') {
              const scrollArea = document.querySelector('.scroll-area');
              lastChild = this.$el.querySelector(`#${lastChildId}`);
              if (lastChild) {
                const style = lastChild.currentStyle || window.getComputedStyle(lastChild);
                const marginTop = parseInt(style.marginTop, 10);
                this.$store.commit('setScrollAreaPosition', {
                  top: (lastChild.offsetTop - (scrollArea.offsetHeight - lastChild.offsetHeight)) + marginTop,
                });
              }
            } else if (direction === 'prev') {
              firstChild = this.$el.querySelector(`#${firstChildId}`);
              if (firstChild) {
                const style = firstChild.currentStyle || window.getComputedStyle(firstChild);
                const marginTop = parseInt(style.marginTop, 10);
                this.$store.commit('setScrollAreaPosition', {
                  top: firstChild.offsetTop - marginTop,
                });
              }
            }

            // Update the scroll bars.
            this.$nextTick(() => {
              this.$store.commit('updateScrollBars');
            });

            return resolve();
          });
        });
      });
    },
  },
};

// Shared code for notifications.
export const notification = {
  props: ['notification'],
  computed: {
    type() {
      const icons = [null, 'friend', 'alert', 'random', 'communication'];
      return icons[this.notification.type || 0];
    },
    text() {
      const n = this.notification;

      let classString = 'username';
      if (n.role === 'dev') {
        classString += ' dev';
      } else if (n.role === 'mod') {
        classString += ' mod';
      }

      const text = n.text.replace('[[USER]]', `<span class="${classString}">${n.user}</span>`);
      return convertEmoji(text);
    },
  },
  methods: {
    action() {
      const n = this.notification;
      const state = this.$store.state;

      if (n.act) {
        const activeTab = state.toolbar.activeTab;
        const prevView = activeTab === 'empty' ? undefined : state[activeTab].activeView;

        switch (n.act) {
          case 'chat':
            this.$store.dispatch('viewChatThread', {
              id: this.notification.chat,
              shouldAddHistory: false,
            });
            break;

          case 'profile':
            // We're going to add our own custom history,
            // so skip adding history when viewing profile.
            this.$store.dispatch('viewProfile', {uid: n.ref, shouldAddHistory: false});
            break;

          case 'forum':
            if (activeTab !== 'forums') {
              this.$store.commit('setTabView', {tab: 'forums', view: 'forums-view'});
              this.$store.dispatch('viewForumThread', {_id: n.ref, shouldAddHistory: false});
            } else if (state.forums.activeThread.id !== n.ref) {
              this.$store.dispatch('viewForumThread', {_id: n.ref, shouldAddHistory: false});
            } else if (!state.scroll.end) {
              this.$store.commit('reloadForumThread');
            } else {
              this.$store.commit('incForumNotifications', 0);
              this.$store.commit('scrollToBottom');
            }
            break;

          case 'feed':
            this.$store.commit('setTabView', {tab: 'forums', view: 'social-feed-update-view'});
            this.$store.dispatch('getSocialFeedUpdate', {id: n.ref});
            break;

          default:
            break;
        }

        if (activeTab !== 'empty') {
          this.$store.commit('addHistory', {
            mutation: 'setTabView',
            data: {
              tab: activeTab,
              view: prevView,
            },
          });
        }
      } else if (n.gid) {
        const game = state.gameData.find(gameData => gameData.gid === n.gid);
        if (game) {
          this.$router.push(`/${game.game}`);
        }
      }
    },
  },
};
